/**
 * @file milkPowderPaste.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-07
 * @modified 2026-09-07
 * @description Turn a supplier's pasted price list into matched products.
 *
 *              The list arrives as a name on one line and a price on the next,
 *              in the supplier's own shorthand — "OPTI LK", "OPTI GOLD +
 *              CLOSTRI", "OPTI 100 > OPTI HS 40" — against stored names like
 *              "Stock - Opti Lamb & Kid 25kg bags". Exact matching gets perhaps
 *              half of them.
 *
 *              So: normalise away the packaging noise, expand the supplier's
 *              abbreviations, and score on token overlap. Everything it decides
 *              is offered for review rather than applied — these are costs, and
 *              a confident wrong match is worse than an obvious gap.
 */

/** Noise that appears in stored names and never in a supplier's list. */
const NOISE = new Set([
  'STOCK', 'BAG', 'BAGS', 'IN', 'THE', 'A', 'X'
]);

/**
 * The supplier's shorthand, expanded to the words the stored names use.
 * Longest keys first at match time so "OPTI GOLD" cannot eat "OPTI GOLD + CL".
 */
const SYNONYMS = [
  ['CLOSTRI', 'CL'],
  ['CLOSTRIDIA', 'CL'],
  ['LK', 'LAMB KID'],
  ['L&K', 'LAMB KID'],
  ['&', 'AND']
];

/** "£1,995" / "1995.00" / "2,445" -> 1995 ; anything else -> null. */
export function parsePrice(line) {
  const t = String(line || '').trim();
  if (!t) return null;
  // A price line is only a price. "OPTI 15" must not become 15.
  if (!/^[£$€]?\s*[\d,]+(\.\d{1,2})?\s*$/.test(t)) return null;
  const n = Number(t.replace(/[£$€,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
}

/**
 * Pair each name with the price that follows it.
 *
 * Driven by which lines ARE prices rather than by counting lines, so a blank
 * line, a heading, or a name and price on the same line all survive. A name
 * with no price before the next name is dropped — there is nothing to apply.
 */
export function parsePaste(text) {
  const out = [];
  let pending = null;

  for (const raw of String(text || '').split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;

    // "OPTI 15  £1,995" on one line.
    const inline = line.match(/^(.*?)[\s\t]+([£$€]\s*[\d,]+(?:\.\d{1,2})?)$/);
    if (inline && parsePrice(inline[2]) !== null && !parsePrice(inline[1])) {
      out.push({ name: inline[1].trim(), price: parsePrice(inline[2]) });
      pending = null;
      continue;
    }

    const price = parsePrice(line);
    if (price !== null) {
      if (pending) { out.push({ name: pending, price }); pending = null; }
      continue;   // a price with no name above it has nothing to attach to
    }
    pending = line;
  }
  return out;
}

/** Comparable token set: upper case, synonyms expanded, noise and sizes gone. */
export function tokenise(name) {
  let s = ` ${String(name || '').toUpperCase()} `;
  for (const [from, to] of SYNONYMS) {
    s = s.split(from).join(` ${to} `);
  }
  return s
    // Pack sizes are packaging, not identity: 25KG, 20Kg Bags, 10kg.
    .replace(/\b\d+\s*KG\b/g, ' ')
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter((t) => t && !NOISE.has(t));
}

/**
 * The candidate names a pasted line should be tried under.
 *
 * "OPTI 100 > OPTI HS 40" is a rename — the supplier saying this product is now
 * called that. Both sides are tried, because the stored name may be either.
 */
export function candidateNames(name) {
  const whole = String(name || '').trim();
  const parts = whole.split(/\s*[>→]\s*/).map((p) => p.trim()).filter(Boolean);
  return parts.length > 1 ? [whole, ...parts] : [whole];
}

function score(pastedTokens, productTokens) {
  if (!pastedTokens.length || !productTokens.length) return 0;
  const p = new Set(productTokens);
  let hit = 0;
  for (const t of pastedTokens) if (p.has(t)) hit++;
  // Against the PASTED tokens: a stored name carries extra words the supplier
  // never writes, and penalising those would push every real match down.
  const coverage = hit / pastedTokens.length;
  // A small nudge towards the tighter product name, so "Opti 100 20kg" beats
  // "Opti 100 + CL 25kg" for a bare "OPTI 100".
  return coverage - (Math.max(0, productTokens.length - hit) * 0.01);
}

/**
 * Match pasted rows onto products.
 *
 * Returns one entry per pasted row, always — an unmatched line has to be
 * visible, because the alternative is a price silently not applied.
 *
 * @param {Array} pasted   [{ name, price }]
 * @param {Array} products [{ inventory_item_id, item_code, item_name }]
 */
export function matchProducts(pasted, products) {
  const prepared = (products || []).map((p) => ({
    product: p,
    tokens: tokenise(`${p.item_name || ''} ${p.item_code || ''}`),
    // Whether this product has ever been priced. Used only to break a TIE —
    // see below.
    priced: p.current_cost != null && p.current_cost !== ''
  }));

  const used = new Set();
  // Best-first across the whole list rather than first-come: two lines can each
  // look plausible against one product, and the stronger claim should win it.
  const scored = [];
  for (let i = 0; i < pasted.length; i++) {
    for (const cand of candidateNames(pasted[i].name)) {
      const tokens = tokenise(cand);
      for (const p of prepared) {
        const s = score(tokens, p.tokens);
        if (s > 0) scored.push({ i, product: p.product, s, priced: p.priced });
      }
    }
  }
  /*
   * Best score first, and on a tie the product that has been priced before.
   *
   * Some names are genuinely ambiguous from the text alone: "Opti Lamb & Kid
   * 10kg" and "Stock - Opti Lamb & Kid 25kg bags" reduce to the same tokens
   * once packaging is stripped, so "OPTI LK" scores identically against both.
   * A product nobody has ever costed is the weaker candidate for a price
   * update, and that is a fact about the product rather than about the price
   * being applied — using the pasted price to choose would be circular, since
   * the price is the thing that is changing.
   *
   * It is still only a tiebreak. The review step is where a wrong one is
   * corrected, and it exists because no rule gets every supplier's shorthand.
   */
  scored.sort((a, b) => (b.s - a.s) || (Number(b.priced) - Number(a.priced)));

  const chosen = new Map();
  for (const row of scored) {
    if (chosen.has(row.i)) continue;
    if (used.has(row.product.inventory_item_id)) continue;
    // Below this the "match" is one shared word like OPTI, which is every
    // product on the list.
    if (row.s < 0.6) continue;
    chosen.set(row.i, { product: row.product, confidence: row.s });
    used.add(row.product.inventory_item_id);
  }

  return pasted.map((row, i) => {
    const hit = chosen.get(i);
    return {
      name: row.name,
      price: row.price,
      product: hit?.product || null,
      confidence: hit ? Math.min(1, hit.confidence) : 0
    };
  });
}

export default { parsePaste, parsePrice, tokenise, candidateNames, matchProducts };
